/**
 * Reset Password Page
 *
 * Allows users to set a new password using the reset token from their email.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { passwordSchema } from '../../../lib/validators/common';
import type { PasswordResetConfirm } from '../../../types/auth.types';
import { useConfirmPasswordReset } from '../hooks/usePasswordReset';
import { getErrorMessage } from '../../../lib/utils/errors';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

const resetPasswordSchema = z.object({
  newPassword: passwordSchema,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);

  const resetToken = searchParams.get('token');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPassword = watch('newPassword');

  const { mutate: confirmReset, isPending } = useConfirmPasswordReset({
    onSuccess: () => {
      setShowSuccess(true);
      setErrorMessage('');
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      if (message.toLowerCase().includes('expired')) {
        setErrorMessage('This reset link has expired. Please request a new password reset.');
      } else if (message.toLowerCase().includes('invalid')) {
        setErrorMessage('This reset link is invalid. Please check your email for the correct link.');
      } else {
        setErrorMessage('Failed to reset password. Please try again.');
      }
    },
  });

  useEffect(() => {
    if (!resetToken) {
      setErrorMessage('No reset token provided. Please check your email for the reset link.');
    }
  }, [resetToken]);

  const onSubmit = (data: ResetPasswordFormData) => {
    if (!resetToken) {
      setErrorMessage('No reset token provided. Please check your email for the reset link.');
      return;
    }

    setErrorMessage('');
    confirmReset({
      resetToken,
      newPassword: data.newPassword,
    });
  };

  // Password strength indicator
  const getPasswordStrength = (password: string): { strength: string; color: string; width: string } => {
    if (!password) return { strength: '', color: '', width: '0%' };

    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { strength: 'Weak', color: 'bg-red-500', width: '33%' };
    if (score <= 4) return { strength: 'Medium', color: 'bg-yellow-500', width: '66%' };
    return { strength: 'Strong', color: 'bg-accent-500', width: '100%' };
  };

  const passwordStrength = getPasswordStrength(newPassword);

  if (showSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-accent-100">
              <CheckCircleIcon className="h-10 w-10 text-accent-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              Password Reset Successful!
            </h2>
            <div className="mt-4 space-y-4">
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Your password has been successfully reset.
              </p>
              <Alert variant="success">
                You can now sign in with your new password.
              </Alert>
            </div>
          </div>

          <div className="text-center">
            <Button
              variant="primary"
              onClick={() => navigate('/login')}
              fullWidth
              size="lg"
            >
              Go to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!resetToken) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              Invalid Reset Link
            </h2>
            <div className="mt-4">
              <Alert variant="error">
                {errorMessage || 'No reset token provided. Please check your email for the reset link.'}
              </Alert>
            </div>
          </div>

          <div className="text-center space-y-2">
            <Link to="/forgot-password">
              <Button variant="secondary" fullWidth>
                Request New Reset Link
              </Button>
            </Link>
            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              or{' '}
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                go back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            Set new password
          </h2>
          <p className="mt-2 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Enter your new password below
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {errorMessage && (
            <Alert variant="error" dismissible onDismiss={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}

          <div className="space-y-4">
            <FormField
              label="New Password"
              required
              error={errors.newPassword?.message}
              helperText="Must be at least 8 characters with uppercase, lowercase, number, and special character"
            >
              <Input
                {...register('newPassword')}
                type="password"
                placeholder="--------"
                error={errors.newPassword?.message}
                disabled={isPending}
              />
              {newPassword && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Password strength:</span>
                    <span className={`text-xs font-medium ${
                      passwordStrength.strength === 'Strong' ? 'text-accent-600' :
                      passwordStrength.strength === 'Medium' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {passwordStrength.strength}
                    </span>
                  </div>
                  <div className="w-full rounded-full h-2" style={{ backgroundColor: 'var(--color-border)' }}>
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                      style={{ width: passwordStrength.width }}
                    />
                  </div>
                </div>
              )}
            </FormField>

            <FormField
              label="Confirm Password"
              required
              error={errors.confirmPassword?.message}
            >
              <Input
                {...register('confirmPassword')}
                type="password"
                placeholder="--------"
                error={errors.confirmPassword?.message}
                disabled={isPending}
              />
            </FormField>
          </div>

          <div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={isPending}
              disabled={isPending}
            >
              Reset Password
            </Button>
          </div>

          <div className="text-center text-sm">
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
